import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import { Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, CreditCard, RefreshCw, Zap, Shield, Share2, Check, X, Star, Crown, Building, User, Loader2, AlertCircle, PieChart, TrendingUp, Gift, Smartphone, Laptop, BarChart3 } from 'lucide-react';
import { useTranslation, useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from '../../App'; 

// Stripe initialization with environment variable
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    
    console.log('Stripe Publishable Key:', publishableKey ? 'Set' : 'Missing');
    
    if (!publishableKey) {
      console.error('Stripe publishable key is missing!');
      throw new Error('Stripe configuration error: Missing publishable key');
    }

    if (!publishableKey.startsWith('pk_')) {
      console.error('Invalid Stripe publishable key format');
      throw new Error('Stripe configuration error: Invalid key format');
    }

    stripePromise = new Promise((resolve, reject) => {
      if (window.Stripe) {
        console.log('Stripe already loaded, initializing...');
        resolve(window.Stripe(publishableKey));
        return;
      }

      console.log('Loading Stripe.js from CDN...');
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        console.log('Stripe.js loaded successfully');
        if (window.Stripe) {
          resolve(window.Stripe(publishableKey));
        } else {
          reject(new Error('Stripe.js failed to load properly'));
        }
      };
      script.onerror = () => {
        console.error('Failed to load Stripe.js');
        reject(new Error('Failed to load Stripe.js'));
      };
      document.head.appendChild(script);
    });
  }
  return stripePromise;
};

const Upgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language: contextLanguage } = useLanguage();
  const { user, httpClient, validateToken } = useAuth(); // Get auth context
  
  // Use localStorage directly as the primary source since context is not working
  const [language, setLanguage] = useState('en');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Mobile detection
  const [unreadCount, setUnreadCount] = useState(0);
  const [userType, setUserType] = useState('individual');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  const [upgradeError, setUpgradeError] = useState('');
  const [upgradeSuccess, setUpgradeSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly'); // monthly or yearly

  // Initialize language from localStorage directly
  useEffect(() => {
    const savedLanguage = localStorage.getItem('easysubs-language');
    console.log('Direct language initialization from localStorage:', savedLanguage);
    
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'it')) {
      setLanguage(savedLanguage);
    } else {
      // Default to English if no valid language found
      setLanguage('en');
      localStorage.setItem('easysubs-language', 'en');
    }
  }, []);

  // Sync with context language when it becomes available
  useEffect(() => {
    if (contextLanguage && (contextLanguage === 'en' || contextLanguage === 'it')) {
      console.log('Syncing with context language:', contextLanguage);
      setLanguage(contextLanguage);
    }
  }, [contextLanguage]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    
    // Redirect to mobile upgrade if on mobile
    if (isMobile) {
      navigate("/mobile-upgrade");
    }
    
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, navigate]);

  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const fetchUserData = async () => {
    try {
      if (!httpClient || !user) {
        console.log('No user or httpClient found');
        setLoading(false);
        return;
      }

      console.log('Fetching user data with token authentication');
      const response = await httpClient.request('/api/user/account');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchUserData();
        }
        throw new Error('Failed to fetch user data');
      }

      const userData = await response.json();
      console.log('User data fetched:', userData);
      setUserData(userData);
      setUserType(userData.userType || 'individual');
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    console.log('Fetching plans with language:', language, 'and userType:', userType);
    
    try {
      if (!httpClient) return;
      
      setPlansLoading(true);
      
      // Build the URL with parameters - language is now always defined
      const url = `/api/plans/${userType}?language=${language}`;
      console.log('API URL:', url);
      
      const response = await httpClient.request(url);
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchPlans();
        }
        
        const errorText = await response.text();
        console.error('API response not OK:', response.status, errorText);
        throw new Error(`Failed to fetch plans: ${response.status}`);
      }

      const plansData = await response.json();
      console.log('Plans received:', plansData.length, 'plans');
      console.log('Plans details:', plansData.map(p => ({ 
        name: p.name, 
        language: p.language, 
        tier: p.tier,
        userType: p.userType 
      })));
      
      setPlans(plansData);
      
      // If no plans found, try fallback
      if (plansData.length === 0) {
        console.log('No plans found with current filters, trying fallback...');
        await fetchPlansFallback();
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      // Try fallback
      await fetchPlansFallback();
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchPlansFallback = async () => {
    try {
      if (!httpClient) return;
      
      console.log('Trying fallback: fetching all plans without language filter...');
      
      const response = await httpClient.request(`/api/plans/${userType}`);
      
      if (!response.ok) {
        throw new Error('Fallback also failed');
      }

      const fallbackPlans = await response.json();
      console.log('Fallback plans received:', fallbackPlans.length, 'plans');
      setPlans(fallbackPlans);
    } catch (fallbackErr) {
      console.error('Fallback also failed:', fallbackErr);
      setPlans([]);
      setUpgradeError('Failed to load plans. Please try again later.');
    }
  };

  // Handle redirects from Stripe
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('success')) {
      setUpgradeSuccess(t('upgrade.paymentSuccess'));
      // Refresh user data to show updated plan
      fetchUserData();
      // Remove query params from URL
      navigate('/upgrade', { replace: true });
    } else if (queryParams.get('canceled')) {
      setUpgradeError(t('upgrade.paymentCanceled'));
      navigate('/upgrade', { replace: true });
    }
  }, [location, navigate, t]);

  useEffect(() => {
    if (!user) {
      navigate("/demo");
      return;
    }
    
    // Don't load data if on mobile (will redirect)
    if (isMobile) {
      return;
    }
    
    fetchUnreadNotifications();
    fetchUserData();
    
    const interval = setInterval(() => {
      fetchUnreadNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user, httpClient, isMobile]);

  useEffect(() => {
    console.log('Plans useEffect triggered:', {
      userType,
      language,
      userDataLoaded: !!userData,
      isMobile
    });
    
    // Don't fetch plans if on mobile
    if (isMobile) {
      return;
    }
    
    // Fetch plans when we have userType and language (language is always defined now)
    if (userType && language && httpClient) {
      fetchPlans();
    } else {
      console.log('Waiting for required data:', {
        userType,
        language,
        httpClient: !!httpClient
      });
    }
  }, [userType, language, httpClient, isMobile]);

  // Return null if mobile (will redirect)
  if (isMobile) {
    return null;
  }

  const navItems = [
    { icon: Home, label: t('bottomNav.home'), path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications'),
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace'), path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs'), path: "/share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank'), path: "/connectBank" },
    { icon: Users, label: t('bottomNav.partners'), path: "/partners" },
    { icon: Settings, label: t('bottomNav.settings'), path: "/account-settings" },
  ];

  const handleUpgrade = async (plan) => {
    if (!httpClient || !user) {
      console.error('No user or httpClient available');
      return;
    }
    
    console.log('Upgrade button clicked for plan:', plan.name);
    console.log('Current language for upgrade:', language);
    console.log('Selected billing interval:', billingInterval);
    
    // Block business accounts from upgrading (coming soon)
    if (userData && userData.userType === 'company') {
      //setUpgradeError(t('upgrade.businessUpgradeComingSoon') || 'Business account upgrades are coming soon.');
      setProcessingPayment(false);
      return;
    }
    
    // Check if user is trying to upgrade to a plan that doesn't match their account type
    if (userData.userType === 'individual' && userType === 'company') {
      setUpgradeError(t('upgrade.personalToBusinessError'));
      return;
    }
    
    if (userData.userType === 'company' && userType === 'individual') {
      setUpgradeError(t('upgrade.businessToPersonalError'));
      return;
    }
    
    setUpgradeError('');
    setUpgradeSuccess('');
    setProcessingPayment(true);

    try {
      // Get the Stripe price ID based on billing interval
      const priceId = billingInterval === 'yearly'
        ? plan.stripe.yearlyPriceId
        : plan.stripe.monthlyPriceId;
      console.log('Stripe Price ID:', priceId);
      
      if (!priceId) {
        setUpgradeError(t('upgrade.planNotAvailable'));
        setProcessingPayment(false);
        return;
      }

      console.log('Creating checkout session...');
      const response = await httpClient.request('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          planId: plan.planId,
          language: language
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return handleUpgrade(plan);
        }
        
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error('Failed to create checkout session');
      }

      const sessionData = await response.json();
      console.log('Session data received:', sessionData);
      
      const sessionId = sessionData.id;
      console.log('Session ID:', sessionId);

      if (!sessionId) {
        throw new Error('No session ID returned from server');
      }
      
      // Initialize and redirect to Stripe Checkout
      console.log('Initializing Stripe...');
      try {
        const stripe = await getStripe();
        console.log('Stripe initialized successfully');
        
        console.log('Redirecting to Stripe Checkout...');
        const { error } = await stripe.redirectToCheckout({ sessionId });
        
        if (error) {
          console.error('Stripe checkout error:', error);
          setUpgradeError(`${t('upgrade.paymentError')}: ${error.message}`);
        } else {
          console.log('Redirect initiated successfully');
        }
      } catch (stripeError) {
        console.error('Stripe initialization failed:', stripeError);
        setUpgradeError(t('upgrade.paymentUnavailable'));
      }
    } catch (err) {
      console.error('Upgrade process failed:', err);
      setUpgradeError(err.message || t('upgrade.paymentUnavailable'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const isUpgradeAllowed = () => {
    return userData && userData.userType === userType;
  };

  const getCurrentPlan = () => {
    if (!userData || !plans.length) return null;
    return plans.find(plan => plan.tier === userData.userPlan);
  };

  const getPlanByTier = (tier) => {
    return plans.find(plan => plan.tier === tier);
  };

  // Show loading only for user data, not for language
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          <p className="text-sm text-gray-500 mt-2">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (plansLoading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          <p className="text-sm text-gray-500 mt-2">Loading plans... (Language: {language})</p>
        </div>
      </div>
    );
  }

  const freePlan = getPlanByTier('free');
  const premiumPlan = getPlanByTier('premium');
  const currentPlan = getCurrentPlan();

  console.log('Rendering with plans:', {
    freePlan: freePlan ? freePlan.name : 'not found',
    premiumPlan: premiumPlan ? premiumPlan.name : 'not found',
    currentPlan: currentPlan ? currentPlan.name : 'not found',
    currentLanguage: language
  });

  // Determine if current user is a business account
  const isBusinessAccount = userData?.userType === 'company';

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={location.pathname}
        navigate={navigate}
        user={user}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} />
        
        <div className="p-6 space-y-6">
          <div className="bg-gradient-to-r from-[#253985] to-amber-500 text-white p-6 rounded-lg">
            <h1 className="text-2xl font-bold mb-2">{t('upgrade.title')}</h1>
            <p className="text-white/90">
            {userData?.userPlan === 'premium' 
            ? t('upgrade.currentPremium')
            : t('upgrade.upgradeFromFree')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <div className="text-center mb-8">
              <Crown className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-3">{t('upgrade.choosePlan')}</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {userType === 'company' 
                  ? t('upgrade.selectPlanDescriptionBusiness')
                  : t('upgrade.selectPlanDescriptionPersonal')}
              </p>
            </div>
            
            {/* Current Plan Indicator */}
            {currentPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-4">
                    <Star className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800">{t('upgrade.yourCurrentPlan')}</h3>
                    <p className="text-blue-600">
                      {currentPlan.name} 
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Success Message */}
            {upgradeSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-green-700">{upgradeSuccess}</p>
                </div>
              </div>
            )}
            
            {/* User Type Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 p-1 rounded-lg inline-flex">
                <button
                  className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                    userType === 'individual' 
                      ? 'bg-white text-[#253985] shadow-sm' 
                      : 'bg-transparent text-gray-600'
                  }`}
                  onClick={() => setUserType('individual')}
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('upgrade.personal')}
                </button>
                <button
                  className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                    userType === 'company' 
                      ? 'bg-white text-[#253985] shadow-sm' 
                      : 'bg-transparent text-gray-600'
                  }`}
                  onClick={() => setUserType('company')}
                >
                  <Building className="h-4 w-4 mr-2" />
                  {t('upgrade.business')}
                </button>
              </div>
            </div>
            
            {/* Upgrade Error Message */}
            {upgradeError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-red-700">{upgradeError}</p>
                </div>
              </div>
            )}
            
            {/* No Plans Found Message */}
            {!freePlan && !premiumPlan && !plansLoading && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                  <div>
                    <p className="text-yellow-700 font-semibold">No plans found</p>
                    <p className="text-yellow-600 text-sm">
                      No plans available for {userType} type in {language} language.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Free Plan */}
              {freePlan && (
                <div 
                  className={`border rounded-2xl p-6 transition-all flex flex-col h-full ${
                    userData?.userPlan === 'free' 
                      ? 'border-[#253985] shadow-lg bg-blue-50' 
                      : 'border-gray-200 hover:shadow-md'
                  }`}
                >
                  {/* All content except button - will grow */}
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Star className="h-6 w-6 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold ml-3">{freePlan.name}</h3>
                      </div>
                      {userData?.userPlan === 'free' && (
                        <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                          {t('upgrade.currentPlan')}
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-6">
                      <span className="text-3xl font-bold">€{freePlan.pricing.monthly.amount}</span>
                      <span className="text-gray-600">{t('common.perMonth')}</span>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {freePlan.features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          {feature.included ? (
                            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                          ) : (
                            <X className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                          )}
                          <span className={feature.included ? "text-gray-700" : "text-gray-400"}>
                            {feature.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <button 
                    className={`w-full py-3 rounded-lg font-medium ${
                      userData?.userPlan === 'free'
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    disabled={userData?.userPlan === 'free'}
                  >
                    {userData?.userPlan === 'free' 
                      ? t('upgrade.currentPlan')
                      : t('upgrade.selectFreePlan')}
                  </button>
                </div>
              )}
              
              {/* Premium Plan */}
              {premiumPlan && (
                <div 
                  className={`border-2 rounded-2xl p-6 transition-all relative flex flex-col h-full ${
                    userData?.userPlan === 'premium' 
                      ? 'border-[#253985] shadow-lg bg-blue-50' 
                      : 'border-gray-200 hover:shadow-md'
                  }`}
                >
                  {premiumPlan.metadata.recommended && (
                    <div className="absolute top-0 right-0 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                      {t('upgrade.recommended')}
                    </div>
                  )}
                  
                  {/* All content except button - will grow */}
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="bg-purple-100 p-2 rounded-lg">
                          <Crown className="h-6 w-6 text-purple-600" />
                        </div>
                        <h3 className="text-xl font-semibold ml-3">{premiumPlan.name}</h3>
                      </div>
                      {userData?.userPlan === 'premium' && (
                        <span className="bg-[#253985] text-white text-xs px-2 py-1 rounded-full">
                          {t('upgrade.currentPlan')}
                        </span>
                      )}
                    </div>
                    
                    {/* Dynamic Price Display based on billing interval */}
                    <div className="mb-4">
                      {billingInterval === 'monthly' ? (
                        <>
                          <span className="text-3xl font-bold">€{premiumPlan.pricing.monthly.amount}</span>
                          <span className="text-gray-600">{t('common.perMonth')}</span>
                          {premiumPlan.pricing.yearly && (
                            <p className="text-sm text-gray-500 mt-1">
                              {t('upgrade.orYearly') || 'or'} €{premiumPlan.pricing.yearly.amount}{t('upgrade.perYear') || '/year'} 
                              {(() => {
                                const monthly = premiumPlan.pricing.monthly.amount;
                                const yearly = premiumPlan.pricing.yearly.amount;
                                const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                                return discount > 0 ? ` (save ${discount}%)` : '';
                              })()}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-bold">€{premiumPlan.pricing.yearly.amount}</span>
                          <span className="text-gray-600">{t('upgrade.perYear') || '/year'}</span>
                          <p className="text-sm text-gray-500 mt-1">
                            {t('upgrade.just') || 'just'} €{(premiumPlan.pricing.yearly.amount / 12).toFixed(2)}
                            {t('common.perMonth') || '/month'} {t('upgrade.whenBilledAnnually') || 'when billed annually'}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Billing Interval Toggle – with floating savings badge */}
                    {premiumPlan.pricing.yearly && (
                      <div className="relative mb-4">
                        {/* Discount badge placed above the yearly side */}
                        {(() => {
                          const monthly = premiumPlan.pricing.monthly.amount;
                          const yearly = premiumPlan.pricing.yearly.amount;
                          const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                          if (discount > 0) {
                            return (
                              <div className="absolute -top-2 right-0 left-1/2 transform -translate-x-1/2 w-max z-10">
                                <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                  Save {discount}%
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* The toggle itself */}
                        <div className="flex bg-gray-100 rounded-lg p-1 relative">
                          <button
                            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                              billingInterval === 'monthly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                            }`}
                            onClick={() => setBillingInterval('monthly')}
                          >
                            {t('upgrade.monthly') || 'Monthly'} · €{premiumPlan.pricing.monthly.amount}/mo
                          </button>
                          <button
                            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                              billingInterval === 'yearly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                            }`}
                            onClick={() => setBillingInterval('yearly')}
                          >
                            {t('upgrade.yearly') || 'Yearly'} · €{premiumPlan.pricing.yearly.amount}/year
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-800 mb-2">
                        {t('upgrade.allFreeFeaturesPlus')}
                      </h4>
                      <ul className="space-y-3">
                        {premiumPlan.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{feature.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <button 
                    className={`w-full py-3 rounded-lg font-medium flex items-center justify-center ${
                      userData?.userPlan === 'premium'
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : !isUpgradeAllowed()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : processingPayment
                        ? 'bg-[#1a2c6d] text-white cursor-not-allowed'
                        : isBusinessAccount
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-[#253985] text-white hover:bg-[#1a2c6d]'
                    }`}
                    disabled={
                      userData?.userPlan === 'premium' ||
                      !isUpgradeAllowed() ||
                      processingPayment ||
                      isBusinessAccount
                    }
                    onClick={() => handleUpgrade(premiumPlan)}
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        {t('upgrade.processing')}
                      </>
                    ) : userData?.userPlan === 'premium' ? (
                      t('upgrade.currentPlan')
                    ) : !isUpgradeAllowed() ? (
                      userData.userType === 'individual' 
                        ? t('upgrade.availableForPersonalOnly')
                        : t('upgrade.availableForBusinessOnly')
                    ) : isBusinessAccount ? (
                      t('upgrade.comingSoon') || 'Coming soon'
                    ) : (
                      userType === 'company' 
                        ? t('upgrade.upgradeToBusiness')
                        : t('upgrade.upgradeToPremium')
                    )}
                  </button>
                  
                  {!premiumPlan.stripe.monthlyPriceId && !premiumPlan.stripe.yearlyPriceId && (
                    <p className="text-xs text-orange-600 mt-2 text-center">
                      {t('upgrade.paymentIntegrationComing')}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {t('upgrade.needChangeAccountType')}
                  </h3>
                  <p className="text-gray-600">
                    {t('upgrade.contactSupportDescription')}
                  </p>
                </div>
                <button 
                  onClick={() => alert(t('upgrade.contactSupportMessage'))}
                  className="px-6 py-3 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] transition-colors"
                >
                  {t('upgrade.contactSupport')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upgrade;